import { FluidRatingIconSize } from '@lmig/fluid-core/lib/structs/enums/fluid-rating-icon-size.enum';
import { h } from '@stencil/core';

import starIcon from '../../../assets/svg/icons/lm/star-fill.svg';
import starHalfFill from '../../../assets/svg/icons/lm/star-half-fill.svg';
import starOutline from '../../../assets/svg/icons/lm/star-outline.svg';
import { EventUtils } from '../../../utils/events/event-utils';

// ================================================================ //
// -- Functional Components (exclusive to FLUID Rating )

export type RatingIconProps = {
  value: number;
  checked: boolean;
  disabled: boolean;
  readonly: boolean;
  fillType: 'fill' | 'empty' | 'partial';
  size: FluidRatingIconSize;
  iconType: 'star' | 'face' | 'heart' | 'radio';
  focusable?: boolean;
  onClickHandler?: (value: number) => void;
};

// TODO: move this to common icon components

export const RatingIcon = (props: RatingIconProps) => {
  const {
    value,
    checked,
    iconType,
    onClickHandler,
    size,
    disabled,
    fillType,
    focusable,
  } = props;

  const tabIndex = disabled ? null : focusable ? 0 : -1;
  const fillIcon =
    fillType === 'fill'
      ? starIcon
      : fillType === 'partial'
      ? starHalfFill
      : starOutline;

  return (
    iconType === 'star' && (
      <button
        role='radio'
        innerHTML={fillIcon}
        disabled={disabled}
        aria-label={`${value} Star${value > 1 ? 's' : ''}`}
        aria-checked={String(checked)}
        tabIndex={tabIndex}
        onClick={() => onClickHandler(value)}
        onKeyDown={(event) => EventUtils.arrowToTab(event, true)}
        class={{
          'rating-icon-container': true,
          [`icon-size-${size}`]: true,
          [`icon-state-${fillType}`]: true,
          'star-disabled': disabled,
        }}
      />
    )
  );
};

export type RateIconListProps = {
  type: 'star' | 'face' | 'heart';
  value: number;
  length: number;
  disabled: boolean;
  readonly: boolean;
  size: FluidRatingIconSize;
  onClickHandler: (value: number) => void;
};
export const RateIconList = (props: RateIconListProps) => {
  const { type, value, length, disabled, readonly, size, onClickHandler } =
    props;
  const _value = isNaN(value) ? 0 : value;

  const computeFillType = (position: number, value: number) => {
    if (value >= position) {
      return 'fill';
    }
    const prevPosition = position - 1;
    if (value < position && value > prevPosition) {
      return 'partial';
    }

    if (value < position) {
      return 'empty';
    }
  };

  const isFocusable = (value: number, index: number) => {
    switch (true) {
      case value === 0 && index === 1:
        return true;
      case value > 0 && index === value:
        return true;
      default:
        return false;
    }
  };

  const rating = Array.from(Array(length).keys());

  return rating.map((i) => {
    const index = i + 1;
    const props: RatingIconProps = {
      value: index,
      checked: index === _value,
      iconType: type,
      disabled,
      readonly,
      size: size,
      fillType: computeFillType(index, _value),
      focusable: isFocusable(_value, index),
      onClickHandler: onClickHandler,
    };

    return <RatingIcon {...props} />;
  });
};
